import axios from 'axios'
import { createSignatureSession as createDbSession, updateSignatureSession } from '../config/database'

// Helper function to call Vercel API route (replaces Supabase Edge Function)
const callFalAPI = async (action, params) => {
  // Use relative URL - Vite proxy handles routing to local server in dev
  // In production, Vercel serves the API route
  const apiUrl = '/api/fal/process-signature'
  
  try {
    const response = await fetch(apiUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ action, ...params })
    })
    
    if (!response.ok) {
      const errorData = await response.text()
      throw new Error(`API route error: ${response.status} ${response.statusText} - ${errorData}`)
    }
    
    return await response.json()
  } catch (error) {
    // Provide helpful error message
    if (error.message.includes('Failed to fetch') || error.message.includes('404')) {
      console.error('API route not found. Make sure the local dev server is running.')
      console.error('Run: npm run dev (this starts both Vite and the API server)')
      throw new Error('API route not available. Make sure the dev server is running with "npm run dev".')
    }
    throw error
  }
}

const signatureStyles = {
  elegant: {
    name: 'Elegant Script',
    description: 'Flowing, sophisticated cursive with graceful flourishes',
    styleModifier: 'elegant cursive script with flowing flourishes and sophisticated loops'
  },
  modern: {
    name: 'Modern Minimal',
    description: 'Clean, contemporary style with subtle curves',
    styleModifier: 'modern minimalist handwriting with clean lines and subtle curves'
  },
  executive: {
    name: 'Executive Bold',
    description: 'Strong, confident strokes with professional authority',
    styleModifier: 'bold executive style with confident, authoritative strokes and strong presence'
  }
}

const generateSignaturePrompt = (name, style) => {
  const styleInfo = signatureStyles[style] || signatureStyles.elegant
  
  const prompt = `A high-resolution, hyper-realistic handwritten signature of "${name}" in pure white ink on a solid black background.

${styleInfo.styleModifier} signature style with natural handwriting characteristics:
– Natural pressure-based stroke variation with thick downstrokes and thin upstrokes
– Dynamic tapering at stroke ends with smooth fade-outs
– Organic letterforms with slight spacing and curvature inconsistencies  
– Trailing flourishes and ligature flow for natural cursive appearance

CRITICAL REQUIREMENTS:
- Pure white ink signature, solid white lines, no shadows, no glow, no gradients, no smudging, clean edges, no glow effect, ink on flat black background
- Background must be solid black (#000000) with absolutely no variations
- No atmospheric effects, lighting, or textures

Professional handwritten signature suitable for branding and business use.`

  return prompt
}

const generateNegativePrompt = () => {
  return "glow, gradient, shadow, blur, soft edges, fog, smudge, glow effects, uneven background, double exposure, background noise, artifact, banding, texture, lighting effects, atmospheric effects, depth, 3d effects, reflections"
}

export const createSignatureSession = async (name, style) => {
  try {
    console.log('Creating signature session with:', { name, style })
    
    const data = await createDbSession({
      name,
      profession: null, // Not using profession for now
      style,
      status: 'submitted'
    })
    
    console.log('Session created successfully:', data)
    return data
  } catch (error) {
    console.error('Error creating signature session:', error)
    throw error
  }
}

export const generateSignatures = async (sessionId, name, style) => {
  try {
    // Update status to generating
    await updateSignatureSession(sessionId, { status: 'generating' })

    const prompt = generateSignaturePrompt(name, style)
    
    // Generate 3 variations with slight randomization
    const variations = await Promise.all([
      generateSingleSignature(prompt + ' Variation 1: slightly more flowing'),
      generateSingleSignature(prompt + ' Variation 2: moderately compact spacing'),
      generateSingleSignature(prompt + ' Variation 3: with subtle extra flourish')
    ])

    // Update session with preview URLs
    await updateSignatureSession(sessionId, {
      preview_urls: variations,
      status: 'preview_ready'
    })

    return variations
  } catch (error) {
    console.error('Error generating signatures:', error)
    throw error
  }
}

const generateSingleSignature = async (prompt) => {
  try {
    const negativePrompt = generateNegativePrompt()
    console.log('🔍 GENERATION: Using Vercel API route for signature generation')
    console.log('🔍 GENERATION: Prompt:', prompt.substring(0, 100) + '...')
    console.log('🔍 GENERATION: Negative prompt:', negativePrompt)
    
    const data = await callFalAPI('generate', { 
      prompt, 
      negativePrompt 
    })

    console.log('✅ GENERATION: API route response:', data)
    
    // Handle different response structures
    if (data.images && data.images.length > 0) {
      return data.images[0].url
    } else if (data.image?.url) {
      return data.image.url
    } else if (data.url) {
      return data.url
    } else {
      throw new Error('No image URL found in API response')
    }
  } catch (error) {
    console.error('❌ GENERATION: Error via API route:', error.message)
    throw error
  }
}

export const processSelectedSignature = async (sessionId, selectedUrl) => {
  try {
    console.log('Processing selected signature:', selectedUrl)
    
    // Update session with selected signature
    await updateSignatureSession(sessionId, {
      selected_url: selectedUrl,
      status: 'processing'
    })

    // Step 1: Upscale the image using AuraSR for better quality
    console.log('Step 1: Upscaling with AuraSR for premium quality...')
    const upscaledUrl = await upscaleImage(selectedUrl)
    
    // Step 2: Remove background to eliminate gradients and get clean transparent PNG
    console.log('Step 2: Removing background to eliminate gradients and artifacts...')
    console.log('Input to background removal:', upscaledUrl)
    const transparentPngUrl = await removeBackground(upscaledUrl)
    console.log('Output from background removal:', transparentPngUrl)
    console.log('Background removal successful:', transparentPngUrl !== upscaledUrl)
    
    // Step 3: Create clean black background version from transparent PNG
    console.log('Step 3: Creating clean black background version...')
    console.log('Input to clean background creation:', transparentPngUrl)
    const cleanBlackUrl = await createCleanBlackBackground(transparentPngUrl)
    console.log('Output from clean background creation:', cleanBlackUrl)
    
    // Step 4: Create SVG version from transparent PNG
    console.log('Step 4: Creating scalable SVG version...')
    const svgUrl = await vectorizeImage(transparentPngUrl)

    // Update session with final URLs
    await updateSignatureSession(sessionId, {
      final_jpg_url: cleanBlackUrl,      // Clean JPG with pure black background
      final_png_url: transparentPngUrl,  // Transparent PNG
      final_svg_url: svgUrl,             // SVG version
      status: 'completed'
    })

    console.log('Processing completed successfully')
    console.log('Final URLs:', {
      jpg: cleanBlackUrl,      // Clean black background
      png: transparentPngUrl,  // Transparent
      svg: svgUrl             // SVG
    })
    
    return {
      jpg: cleanBlackUrl,      // Clean black background version
      png: transparentPngUrl,  // Transparent version
      svg: svgUrl             // SVG version
    }
  } catch (error) {
    console.error('Error processing selected signature:', error)
    throw error
  }
}

const upscaleImage = async (imageUrl) => {
  try {
    console.log('🔍 UPSCALING: Using Vercel API route for upscaling:', imageUrl)
    
    const data = await callFalAPI('upscale', { imageUrl })
    
    console.log('🔍 UPSCALING: API route response:', data)
    
    const upscaledUrl = data.image?.url || data.url || data.images?.[0]?.url
    
    if (upscaledUrl && upscaledUrl !== imageUrl) {
      console.log('✅ UPSCALING: Success! New URL:', upscaledUrl)
      return upscaledUrl
    } else {
      console.error('❌ UPSCALING: API route returned same URL or no URL')
      console.log('🔍 UPSCALING: Falling back to original image')
      return imageUrl
    }
  } catch (error) {
    console.error('❌ UPSCALING: Error with API route:', error.message)
    console.log('🔍 UPSCALING: Falling back to original image')
    return imageUrl
  }
}

const removeBackground = async (imageUrl) => {
  try {
    console.log('🔍 BACKGROUND REMOVAL: Starting with manual approach for better artifact handling:', imageUrl)
    
    // Skip Fal.ai rembg and go straight to advanced manual processing
    // because rembg doesn't handle gradients/artifacts well in AI-generated images
    console.log('🔍 BACKGROUND REMOVAL: Using advanced manual processing for gradient removal...')
    return await createAdvancedTransparentVersion(imageUrl)
    
    /* OLD CODE - Fal.ai rembg doesn't handle gradients well
    const data = await callEdgeFunction('remove_background', { imageUrl })
    console.log('🔍 BACKGROUND REMOVAL: Edge Function response:', data)
    const pngUrl = data.image?.url || data.url || data.images?.[0]?.url
    
    if (pngUrl && pngUrl !== imageUrl) {
      console.log('✅ BACKGROUND REMOVAL: Edge Function Success! New URL:', pngUrl)
      // Even if Fal.ai succeeds, still process with manual method for gradient removal
      return await createAdvancedTransparentVersion(pngUrl)
    } else {
      console.log('🔍 BACKGROUND REMOVAL: Falling back to manual approach...')
      return await createAdvancedTransparentVersion(imageUrl)
    }
    */
  } catch (error) {
    console.error('❌ BACKGROUND REMOVAL: Error:', error.message)
    console.log('🔍 BACKGROUND REMOVAL: Falling back to manual approach...')
    return await createAdvancedTransparentVersion(imageUrl)
  }
}

const createAdvancedTransparentVersion = async (imageUrl) => {
  try {
    console.log('🔍 ADVANCED TRANSPARENCY: Starting advanced background removal:', imageUrl)
    
    return new Promise((resolve, reject) => {
      const canvas = document.createElement('canvas')
      const ctx = canvas.getContext('2d')
      const img = new Image()
      
      img.crossOrigin = 'anonymous'
      img.onload = () => {
        canvas.width = img.width
        canvas.height = img.height
        
        console.log('🔍 ADVANCED TRANSPARENCY: Canvas size:', canvas.width, 'x', canvas.height)
        
        // Draw the original image
        ctx.drawImage(img, 0, 0)
        
        // Get image data for pixel manipulation
        const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height)
        const data = imageData.data
        
        console.log('🔍 ADVANCED TRANSPARENCY: Processing', data.length / 4, 'pixels')
        
        // ENHANCED ALGORITHM for aggressive gradient and artifact removal
        for (let i = 0; i < data.length; i += 4) {
          const red = data[i]
          const green = data[i + 1]
          const blue = data[i + 2]
          const alpha = data[i + 3]
          
          // Calculate various color metrics
          const brightness = (red * 0.299 + green * 0.587 + blue * 0.114)
          const saturation = Math.max(red, green, blue) - Math.min(red, green, blue)
          const isGrayish = saturation < 40 // Low saturation indicates gray/black/white
          const avgColor = (red + green + blue) / 3
          
          // Check if pixel is close to black/gray (background artifacts)
          const isNearBlack = brightness < 80  // More aggressive threshold
          const isGrayBanding = isGrayish && brightness < 150  // Catch gray banding
          const hasArtifacts = Math.abs(red - green) < 20 && Math.abs(green - blue) < 20 && avgColor < 120
          
          // Multi-criteria approach for aggressive artifact removal
          let shouldMakeTransparent = false
          
          // PRIMARY: Remove all dark pixels (black background and gradients)
          if (isNearBlack) {
            shouldMakeTransparent = true
          }
          // SECONDARY: Remove gray banding and artifacts  
          else if (isGrayBanding || hasArtifacts) {
            shouldMakeTransparent = true
          }
          // TERTIARY: Handle anti-aliased edges more aggressively
          else if (brightness < 200 && isGrayish) {
            // More aggressive alpha calculation for cleaner edges
            const newAlpha = Math.max(0, (brightness - 80) / 120 * 255)
            data[i + 3] = Math.min(alpha, newAlpha)
          }
          // QUATERNARY: Remove any remaining grayish pixels that could be artifacts
          else if (brightness < 160 && saturation < 20) {
            // These are likely artifacts - make them semi-transparent based on brightness
            const newAlpha = Math.max(0, (brightness - 100) / 60 * 255)
            data[i + 3] = Math.min(alpha, newAlpha)
          }
          
          if (shouldMakeTransparent) {
            data[i + 3] = 0  // Make completely transparent
          }
        }
        
        // Put the modified image data back
        ctx.putImageData(imageData, 0, 0)
        
        console.log('🔍 ADVANCED TRANSPARENCY: First pass complete, applying second pass for stubborn artifacts...')
        
        // SECOND PASS: Apply additional cleaning for any remaining artifacts
        const secondPassData = ctx.getImageData(0, 0, canvas.width, canvas.height)
        const secondData = secondPassData.data
        
        for (let i = 0; i < secondData.length; i += 4) {
          const red = secondData[i]
          const green = secondData[i + 1]
          const blue = secondData[i + 2]
          const alpha = secondData[i + 3]
          
          // Skip if already transparent
          if (alpha === 0) continue
          
          const brightness = (red * 0.299 + green * 0.587 + blue * 0.114)
          
          // More aggressive cleanup for remaining artifacts
          if (brightness < 100) {
            secondData[i + 3] = 0  // Remove any remaining dark pixels
          } else if (brightness < 180) {
            // For medium-dark pixels, reduce alpha significantly
            const newAlpha = Math.max(0, (brightness - 100) / 80 * 255)
            secondData[i + 3] = Math.min(alpha, newAlpha * 0.7) // More aggressive
          }
        }
        
        // Apply the second pass
        ctx.putImageData(secondPassData, 0, 0)
        
        console.log('🔍 ADVANCED TRANSPARENCY: Second pass complete - all artifacts should be removed')
        
        // Convert to blob and create URL
        canvas.toBlob((blob) => {
          if (blob) {
            const transparentUrl = URL.createObjectURL(blob)
            console.log('✅ ADVANCED TRANSPARENCY: Success! Created blob size:', blob.size)
            resolve(transparentUrl)
          } else {
            console.error('❌ ADVANCED TRANSPARENCY: Failed to create blob')
            resolve(imageUrl) // Fallback to original
          }
        }, 'image/png')
      }
      
      img.onerror = (error) => {
        console.error('❌ ADVANCED TRANSPARENCY: Failed to load image:', error)
        resolve(imageUrl) // Fallback to original
      }
      
      img.src = imageUrl
    })
  } catch (error) {
    console.error('❌ ADVANCED TRANSPARENCY: Error:', error)
    return imageUrl // Fallback to original
  }
}

// Keep the old function as backup
const createManualTransparentVersion = async (imageUrl) => {
  console.log('🔍 SIMPLE TRANSPARENCY: Using simple threshold approach')
  return await createAdvancedTransparentVersion(imageUrl)
}

const createCleanBlackBackground = async (transparentImageUrl) => {
  try {
    console.log('🔍 CLEAN BACKGROUND: Starting black background creation:', transparentImageUrl)
    
    // Create a canvas to composite transparent signature onto pure black background
    return new Promise((resolve, reject) => {
      const canvas = document.createElement('canvas')
      const ctx = canvas.getContext('2d')
      const img = new Image()
      
      img.crossOrigin = 'anonymous'
      img.onload = () => {
        // Set canvas dimensions to match the image
        canvas.width = img.width
        canvas.height = img.height
        
        console.log('🔍 CLEAN BACKGROUND: Canvas dimensions:', canvas.width, 'x', canvas.height)
        
        // Fill with pure black background first
        ctx.fillStyle = '#000000'
        ctx.fillRect(0, 0, canvas.width, canvas.height)
        
        console.log('🔍 CLEAN BACKGROUND: Black background filled')
        
        // Set blend mode to ensure proper compositing
        ctx.globalCompositeOperation = 'source-over'
        
        // Draw the transparent signature on top
        ctx.drawImage(img, 0, 0)
        
        console.log('🔍 CLEAN BACKGROUND: Transparent image composited')
        
        // Convert to blob and create URL
        canvas.toBlob((blob) => {
          if (blob) {
            const cleanUrl = URL.createObjectURL(blob)
            console.log('✅ CLEAN BACKGROUND: Success! Blob size:', blob.size)
            console.log('🔍 CLEAN BACKGROUND: Generated URL:', cleanUrl.substring(0, 50) + '...')
            resolve(cleanUrl)
          } else {
            console.error('❌ CLEAN BACKGROUND: Failed to create blob from canvas')
            resolve(transparentImageUrl) // Fallback to transparent image
          }
        }, 'image/jpeg', 0.95)
      }
      
      img.onerror = (error) => {
        console.error('❌ CLEAN BACKGROUND: Failed to load transparent image:', error)
        resolve(transparentImageUrl) // Fallback to transparent image
      }
      
      img.src = transparentImageUrl
    })
  } catch (error) {
    console.error('❌ CLEAN BACKGROUND: Error:', error)
    console.log('🔍 CLEAN BACKGROUND: Falling back to transparent image')
    return transparentImageUrl // Return transparent if compositing fails
  }
}

const vectorizeImage = async (imageUrl) => {
  try {
    console.log('🔍 SVG: Creating SVG with embedded base64 image:', imageUrl)
    
    // Convert blob URL to base64 data URL for embedding in SVG
    const base64DataUrl = await convertBlobToBase64(imageUrl)
    
    if (!base64DataUrl) {
      console.error('❌ SVG: Failed to convert to base64')
      return imageUrl
    }
    
    console.log('🔍 SVG: Base64 conversion successful, length:', base64DataUrl.length)
    
    // Create SVG with embedded base64 image (preserves transparency)
    const svgContent = `<?xml version="1.0" encoding="UTF-8"?>
<svg xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" 
     width="5376" height="3072" viewBox="0 0 5376 3072">
  <image href="${base64DataUrl}" x="0" y="0" width="5376" height="3072" />
</svg>`
    
    // Convert SVG content to data URL
    const svgDataUrl = `data:image/svg+xml;base64,${btoa(svgContent)}`
    
    console.log('✅ SVG: Successfully created SVG with embedded image')
    return svgDataUrl
  } catch (error) {
    console.error('❌ SVG: Error creating SVG:', error)
    console.log('🔍 SVG: Falling back to original image')
    return imageUrl // Return original if SVG creation fails
  }
}

const convertBlobToBase64 = async (blobUrl) => {
  try {
    console.log('🔍 BASE64: Converting blob to base64:', blobUrl)
    
    // If it's already a data URL, return it
    if (blobUrl.startsWith('data:')) {
      console.log('🔍 BASE64: Already a data URL')
      return blobUrl
    }
    
    // Fetch the blob and convert to base64
    const response = await fetch(blobUrl)
    const blob = await response.blob()
    
    return new Promise((resolve) => {
      const reader = new FileReader()
      reader.onload = () => {
        const base64 = reader.result
        console.log('✅ BASE64: Conversion successful, size:', base64.length)
        resolve(base64)
      }
      reader.onerror = () => {
        console.error('❌ BASE64: FileReader error')
        resolve(null)
      }
      reader.readAsDataURL(blob)
    })
  } catch (error) {
    console.error('❌ BASE64: Error converting blob to base64:', error)
    return null
  }
}

export { signatureStyles }